import { MARKETING_PAGES, SITE_URL } from '../screens/marketing/marketingData'

export const GA4_ID = 'G-9DYXQDP0VK'
const CONSENT_KEY = 'axxiz_marketing_analytics_v1'
type Consent = 'granted' | 'denied' | null
let memoryConsent: Consent = null
let initialized = false
let lastPage: string | null = null

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

export function ga4Available() {
  return typeof window !== 'undefined' && window.location.protocol === 'https:' &&
    ['www.autoaxxiz.co.za', 'autoaxxiz.co.za'].includes(window.location.hostname) &&
    navigator.doNotTrack !== '1' &&
    !('globalPrivacyControl' in navigator && navigator.globalPrivacyControl === true)
}

export function analyticsConsent(): Consent {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    return stored === 'granted' || stored === 'denied' ? stored : memoryConsent
  } catch { return memoryConsent }
}

function disable(disabled: boolean) {
  Object.assign(window, { [`ga-disable-${GA4_ID}`]: disabled })
}

function referrerOrigin() {
  try {
    const url = new URL(document.referrer)
    return ['http:', 'https:'].includes(url.protocol) ? url.origin : ''
  } catch { return '' }
}

function initialize() {
  disable(false)
  if (initialized) return
  initialized = true
  window.dataLayer ??= []
  // eslint-disable-next-line prefer-rest-params -- Google's loader requires Arguments command records.
  window.gtag = function () { window.dataLayer!.push(arguments) }
  window.gtag('consent', 'default', {
    analytics_storage: 'granted', ad_storage: 'denied',
    ad_user_data: 'denied', ad_personalization: 'denied',
  })
  window.gtag('js', new Date())
  window.gtag('config', GA4_ID, {
    send_page_view: false, allow_google_signals: false,
    allow_ad_personalization_signals: false, cookie_domain: window.location.hostname,
    page_location: `${SITE_URL}${window.location.pathname}`, page_referrer: referrerOrigin(),
  })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
  document.head.appendChild(script)
}

export function setAnalyticsConsent(consent: Exclude<Consent, null>) {
  memoryConsent = consent
  try { localStorage.setItem(CONSENT_KEY, consent) } catch { /* Retain this session's choice. */ }
  disable(consent !== 'granted')
  if (consent === 'denied') {
    lastPage = null
    // Remove this host's Analytics cookies when the visitor withdraws consent.
    for (const cookie of document.cookie.split(';')) {
      const name = cookie.trim().split('=')[0]
      if (name === '_ga' || name.startsWith('_ga_')) {
        for (const domain of ['', `; domain=${window.location.hostname}`]) {
          document.cookie = `${name}=; Max-Age=0; path=/${domain}; Secure; SameSite=Lax`
        }
      }
    }
  } else {
    trackGa4('Marketing Page View')
  }
  window.dispatchEvent(new Event('axxiz-analytics-consent'))
}

export function trackGa4(event: string) {
  if (!ga4Available() || analyticsConsent() !== 'granted') {
    if (typeof window !== 'undefined') disable(true)
    return
  }
  const path = window.location.pathname.replace(/\/$/, '') || '/'
  const meta = MARKETING_PAGES[path]
  if (!meta) { disable(true); lastPage = null; return }
  const names: Record<string, string> = {
    'Marketing Page View': 'page_view',
    'Marketing CTA Click': 'marketing_cta_click',
    'Marketing Lead Submitted': 'generate_lead',
  }
  const name = names[event]
  if (!name || (name === 'page_view' && lastPage === path)) return
  initialize()
  if (name === 'page_view') lastPage = path
  window.gtag?.('event', name, {
    send_to: GA4_ID, page_location: `${SITE_URL}${path}`,
    page_title: meta.title, page_referrer: referrerOrigin(),
  })
}

export function pauseGa4() {
  if (typeof window !== 'undefined') disable(true)
  lastPage = null
}
